/**
 * Paint a paused `<video>`'s first frame cross-platform — including mobile.
 *
 * Desktop browsers decode + paint the frame at the current position on their own
 * (or from a programmatic `currentTime` seek), so `loadeddata` fires and a still
 * frame shows while paused. Mobile browsers don't: they downgrade `preload` and
 * refuse to decode a frame from a programmatic seek on a paused element without a
 * user gesture, so it stays black and `loadeddata` never fires until the user
 * taps play. That's the "stuck black stage / face preview spins forever, then
 * works the instant I hit play" bug.
 *
 * The muted-autoplay policy *does* permit a programmatic muted `play()`, so a
 * brief muted play→pause kick forces the first frame to decode + paint and fires
 * `loadeddata`/`canplay`. We seek to `targetSec` (floored a hair past 0 so even
 * the very first frame paints) so the painted frame is the intended one. The
 * element is muted only for the kick and restored afterwards; it ends paused, so
 * no audio plays even on an unmuted stimulus.
 *
 * Fast-path no-op once the element already has a decoded frame
 * (`readyState >= HAVE_CURRENT_DATA`): on desktop that's the common case, so this
 * never touches mute or playback there.
 *
 * `stayPaused` guards the tiny window where the user taps play mid-kick: if it
 * returns false by then we leave the element playing and restore the caller's
 * mute, so the kick can't strand intended playback in a paused/muted state.
 */
export async function primeFirstFrame(
  v: HTMLVideoElement,
  targetSec: number,
  stayPaused: () => boolean = () => true,
): Promise<void> {
  const seek = () => {
    const dur = v.duration
    const cap = Number.isFinite(dur) && dur > 0 ? Math.min(targetSec, dur) : targetSec
    const goal = Math.max(cap, 0.001)
    if (Math.abs(v.currentTime - goal) > 0.001) {
      try {
        v.currentTime = goal
      } catch {
        /* not seekable yet — the caller's seek-on-drift effect will retry */
      }
    }
  }

  seek()
  // Already have a decoded frame (desktop / fully buffered) — nothing to force.
  if (v.readyState >= 2 /* HAVE_CURRENT_DATA */) return

  const wasMuted = v.muted
  try {
    v.muted = true
    await v.play()
    if (stayPaused()) v.pause()
  } catch {
    /* gesture still required — the on-screen play button will paint it */
  } finally {
    v.muted = wasMuted
    if (stayPaused()) seek()
  }
}
